import { useEffect, useState, useSyncExternalStore } from 'react';
import { CloudArrowUp, DownloadSimple } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { ORIGIN_LABEL, OUTBOX_MAX_AGE_HOURS, TILE_LABEL, outboxVisibleTo, type Tile } from '@helpdesk/shared';
import { meQuery } from '../api/queries.ts';
import { fmtDateTime } from '../lib/format.ts';
import {
  discardReport,
  outboxSnapshot,
  retryReport,
  subscribeOutbox,
  type ExpiredReport,
  type OutboxState,
  type PendingReport,
  type QueuedReport,
} from '../lib/outbox.ts';
import { Badge, Button, Modal, Notice, type BadgeTone } from './ui.tsx';

/**
 * Sichtbarer Ausgang: Zähler im Kopf, dahinter die Liste der noch nicht gesendeten
 * Meldungen. Hier erfährt der Melder, warum eine Meldung liegt, und entscheidet,
 * was mit einer abgelehnten geschieht.
 */
const STATE_LABEL: Record<OutboxState, string> = {
  waiting: 'Noch nicht gesendet',
  held: 'Wartet auf Anmeldung',
  rejected: 'Abgelehnt',
  expired: 'Verfallen',
};

const STATE_TONE: Record<OutboxState, BadgeTone> = { waiting: 'warn', held: 'warn', rejected: 'error', expired: 'error' };

/** Schnellmeldung und Kiosk hängen an keinem Konto: Sie warten auf das Firmennetz, „bitte anmelden“ hülfe dort den wenigsten. */
const stateOf = (report: PendingReport): OutboxState => (report.state === 'held' && report.kind !== 'web' ? 'waiting' : (report.state ?? 'waiting'));

const isUnowned = (report: PendingReport) => report.state !== 'expired' && report.kind === 'web' && !report.ownerUserId;

const photoCount = (n: number) => (n === 1 ? '1 Foto' : `${n} Fotos`);

const reportsAre = (n: number, detail = '') => (n === 1 ? `1 Meldung${detail} ist` : `${n} Meldungen${detail} sind`);

export const useOutbox = () => useSyncExternalStore(subscribeOutbox, outboxSnapshot);

/** Die Reihenfolge ist der Rang: Was die Person selbst klären muss, steht vorn. */
const COUNTER_LABEL = {
  rejected: 'abgelehnt',
  expired: 'verfallen',
  unowned: 'bitte prüfen',
  held: 'bitte anmelden',
  waiting: 'noch nicht gesendet',
} as const;

type CounterGroup = keyof typeof COUNTER_LABEL;

const COUNTER_GROUPS = Object.keys(COUNTER_LABEL) as CounterGroup[];

/** Jeder Eintrag zählt in genau einer Gruppe. */
const groupOf = (report: PendingReport): CounterGroup => {
  const state = stateOf(report);
  return state !== 'rejected' && isUnowned(report) ? 'unowned' : state;
};

function counterParts(reports: PendingReport[]) {
  return COUNTER_GROUPS.flatMap((group) => {
    const members = reports.filter((r) => groupOf(r) === group);
    return members[0] ? [{ group, text: `${members.length} ${COUNTER_LABEL[group]}`, since: members[0].createdAt }] : [];
  });
}

type CounterPart = ReturnType<typeof counterParts>[number];

const hasRejected = (parts: CounterPart[]) => parts.some((p) => p.group === 'rejected' || p.group === 'expired');

/** Kurzform für den Kopf: nur die dringendste Gruppe, dahinter liegt die Liste. */
export function counterText(reports: PendingReport[]): { text: string; rejected: boolean } {
  const parts = counterParts(reports);
  return { text: parts[0]?.text ?? '', rejected: hasRejected(parts) };
}

/** Ohne `AppShell` gibt es keine Liste: Eine abgelehnte Meldung darf die wartenden dort nicht verdecken. */
export function counterDetail(reports: PendingReport[]): { text: string; rejected: boolean } {
  const parts = counterParts(reports);
  const withSince = ({ group, text, since }: CounterPart) => (group === 'waiting' || group === 'held' ? `${text} (gespeichert seit ${fmtDateTime(since)})` : text);
  return { text: parts.map(withSince).join(', '), rejected: hasRejected(parts) };
}

/** Eine Meldung kann nur aus Fotos bestehen: Ohne Vorschau bliebe beim Verwerfen unklar, was verloren geht. */
function PhotoPreview({ photo, position, label }: { photo: Blob; position: number; label: string }) {
  const [url, setUrl] = useState<string>();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const created = URL.createObjectURL(photo);
    setUrl(created);
    return () => URL.revokeObjectURL(created);
  }, [photo]);
  if (!url) return null;
  return (
    <li>
      <button type="button" className="block cursor-zoom-in" onClick={() => setOpen(true)}>
        <img src={url} alt={label} className="h-24 w-24 border border-[var(--border-default)] object-cover" />
      </button>
      <Modal
        open={open}
        closeOnBackdrop
        title={label}
        onClose={() => setOpen(false)}
        footer={
          // Was `resizePhoto` nicht umwandeln konnte, etwa HEIC, bleibt ein File mit eigener Endung. Der Rest ist JPEG.
          <a className="ad-button-outline ad-button-sm inline-flex items-center gap-2" href={url} download={photo instanceof File ? photo.name : `foto-${position}.jpg`}>
            <DownloadSimple size={18} aria-hidden />
            Herunterladen
          </a>
        }
      >
        <img src={url} alt={label} className="mx-auto max-h-[72dvh] w-auto max-w-full" />
      </Modal>
    </li>
  );
}

function useCardAction() {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const act = async (action: () => Promise<void>) => {
    setBusy(true);
    setFailed(false);
    try {
      await action();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return { busy, failed, act };
}

const DEVICE_FAILURE = 'Das hat auf diesem Gerät nicht geklappt. Bitte die App neu öffnen und erneut versuchen.';

function ExpiredCard({ report }: { report: ExpiredReport }) {
  const { busy, failed, act } = useCardAction();
  return (
    <li className="space-y-2 border border-[var(--border-default)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge tone={STATE_TONE.expired}>{STATE_LABEL.expired}</Badge>
        <span className="text-[14px] text-[var(--text-muted)]">{fmtDateTime(report.createdAt)}</span>
      </div>
      <p className="font-semibold text-steel-900">{report.machineLabel ?? '-'}</p>
      <p className="text-[14px] text-[var(--text-muted)]">{ORIGIN_LABEL[report.kind]}</p>
      <p>
        {report.number
          ? `Die Fotos zu ${report.number} wurden nicht übertragen und nach ${OUTBOX_MAX_AGE_HOURS} Stunden von diesem Gerät gelöscht. Das Ticket bleibt bestehen.`
          : `Diese Meldung wurde nicht gesendet und nach ${OUTBOX_MAX_AGE_HOURS} Stunden von diesem Gerät gelöscht. Bitte bei Bedarf neu melden.`}
      </p>
      {failed ? <Notice tone="warn">{DEVICE_FAILURE}</Notice> : null}
      <div className="pt-1">
        <Button disabled={busy} onClick={() => void act(() => discardReport(report.id))}>
          Bestätigen
        </Button>
      </div>
    </li>
  );
}

function ReportCard({ report, userId }: { report: QueuedReport; userId: string }) {
  const qc = useQueryClient();
  const { busy, failed, act } = useCardAction();
  const state = stateOf(report);
  const unowned = isUnowned(report);
  // Am Hallen-Tablet melden wechselnde Personen: Wer sich später hier anmeldet, sieht ihren Text nicht.
  const showContent = report.kind !== 'kiosk';
  const description = typeof report.body.description === 'string' ? report.body.description : '';
  const title = `${report.number ? `${report.number}: ` : ''}${TILE_LABEL[report.body.tile as Tile]}`;
  const openPhotos = report.photos.slice(report.uploaded ?? 0);
  const failedPhotos = report.failedPhotos ?? [];
  const photos = [...openPhotos, ...failedPhotos.map((f) => f.photo)];

  const retry = async () => {
    await retryReport(report.id, userId);
    // Die Sitzung kann längst abgelaufen sein, ohne dass die Seite es merkt: So führt ein 401 zur Anmeldung.
    await qc.invalidateQueries({ queryKey: meQuery.queryKey });
  };

  const discard = () => {
    const question = report.ticketId
      ? 'Die nicht übertragenen Fotos verwerfen? Das Ticket bleibt bestehen.'
      : 'Diese Meldung endgültig verwerfen? Text und Fotos sind dann weg.';
    if (window.confirm(question)) void act(() => discardReport(report.id));
  };

  return (
    <li className="space-y-2 border border-[var(--border-default)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge tone={STATE_TONE[state]}>{state === 'rejected' && report.ticketId ? 'Foto abgelehnt' : unowned && state !== 'rejected' ? 'Bitte prüfen' : STATE_LABEL[state]}</Badge>
        <span className="text-[14px] text-[var(--text-muted)]">{fmtDateTime(report.createdAt)}</span>
      </div>
      <p className="font-semibold text-steel-900">{title}</p>
      {report.machineLabel ? <p className="text-[14px] text-[var(--text-muted)]">{report.machineLabel}</p> : null}
      {showContent && description ? <p className="break-words whitespace-pre-wrap">{description}</p> : null}
      {showContent && photos.length ? (
        <ul className="flex flex-wrap gap-2">
          {photos.map((photo, i) => (
            <PhotoPreview key={i} photo={photo} position={i + 1} label={`Foto ${i + 1} von ${photos.length} zu ${title}`} />
          ))}
        </ul>
      ) : null}
      {openPhotos.length ? <p className="text-[14px] text-[var(--text-muted)]">{photoCount(openPhotos.length)} gespeichert</p> : null}
      {unowned ? <Notice tone="warn">Diese Meldung stammt aus einer älteren Version der App und ist keinem Konto zugeordnet. „Erneut senden“ meldet sie unter Ihrem Namen.</Notice> : null}
      {report.reason ? <Notice tone={state === 'waiting' ? 'info' : 'warn'}>{report.reason}</Notice> : null}
      {failedPhotos.length ? (
        <Notice tone="warn">
          {photoCount(failedPhotos.length)} nicht übertragen: {[...new Set(failedPhotos.map((f) => f.reason))].join(' ')}
        </Notice>
      ) : null}
      {failed ? <Notice tone="warn">{DEVICE_FAILURE}</Notice> : null}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button disabled={busy} onClick={() => void act(retry)}>
          {busy ? 'Einen Moment …' : 'Erneut senden'}
        </Button>
        <Button variant="outline" disabled={busy} onClick={discard}>
          Verwerfen
        </Button>
      </div>
    </li>
  );
}

export function OutboxPanel({ userId }: { userId: string }) {
  const reports = useOutbox().reports.filter((r) => outboxVisibleTo(r, userId));
  const [open, setOpen] = useState(false);
  const empty = reports.length === 0;
  useEffect(() => {
    if (empty) setOpen(false);
  }, [empty]);
  if (empty) return null;

  const { text, rejected } = counterText(reports);
  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        title="Meldungen im Ausgang ansehen"
        onClick={() => setOpen(true)}
        className={`inline-flex min-h-9 min-w-0 cursor-pointer items-center gap-1.5 px-2.5 py-1 text-[14px] font-semibold text-white ${rejected ? 'bg-error' : 'bg-warn'}`}
      >
        <CloudArrowUp size={18} weight="bold" className="shrink-0" />
        <span className="truncate">{text}</span>
      </button>
      <Modal open={open} title="Meldungen im Ausgang" onClose={() => setOpen(false)} closeOnBackdrop>
        <p className="text-[var(--text-muted)]">
          Diese Meldungen liegen nur auf diesem Gerät. Sie werden gesendet, sobald die App wieder Verbindung hat, abgelehnte und nicht zugeordnete erst auf Ihren Klick. Nach {OUTBOX_MAX_AGE_HOURS} Stunden
          werden Text und Fotos gelöscht.
        </p>
        <ul className="space-y-3">
          {reports.map((report) => (report.state === 'expired' ? <ExpiredCard key={report.id} report={report} /> : <ReportCard key={report.id} report={report} userId={userId} />))}
        </ul>
      </Modal>
    </>
  );
}

/**
 * Auf der Anmeldeseite gibt es keinen Kopf: Nur die Zahlen, ohne Inhalt, das Gerät kann geteilt sein.
 * Eine Zusage hängt an der Art: Nur `web` mit Besitzer wartet auf eine bestimmte Person.
 */
export function OutboxLoginHint() {
  const waiting = useOutbox().reports.filter((r) => stateOf(r) === 'waiting' || stateOf(r) === 'held');
  const unowned = waiting.filter(isUnowned).length;
  const owned = waiting.filter((r) => r.kind === 'web').length - unowned;
  const anonymous = waiting.length - owned - unowned;
  return (
    <>
      {owned ? (
        <Notice tone="warn">
          {reportsAre(owned)} noch nicht gesendet. {owned === 1 ? 'Sie geht' : 'Sie gehen'} erst raus, wenn sich die Person anmeldet, die sie gespeichert hat.
        </Notice>
      ) : null}
      {anonymous ? <Notice tone="warn">{reportsAre(anonymous, ' ohne Anmeldung')} noch nicht gesendet.</Notice> : null}
      {unowned ? <Notice tone="warn">{reportsAre(unowned, ' aus einer älteren Version der App')} keinem Konto zugeordnet. Bitte nach der Anmeldung im Ausgang prüfen.</Notice> : null}
    </>
  );
}

/**
 * Kiosk und Schnellmeldung ohne Anmeldung: Verfallene bestätigen, den Zähler zeigt `OutboxAgent`.
 * Das Gerät gehört allen, darum nur Anzahl und Uhrzeit, nie Name oder Text.
 */
export function OutboxDeviceNotice({ kind }: { kind: 'quick' | 'kiosk' }) {
  const { busy, failed, act } = useCardAction();
  const expired = useOutbox().reports.filter((r) => r.kind === kind && r.state === 'expired');
  if (!expired.length) return null;

  const confirmExpired = async () => {
    await Promise.all(expired.map((r) => discardReport(r.id)));
  };

  return (
    <div className="mb-5 space-y-2">
      <Notice tone="warn">
        {expired.length === 1 ? '1 Meldung wurde' : `${expired.length} Meldungen wurden`} nicht vollständig gesendet und nach {OUTBOX_MAX_AGE_HOURS} Stunden von diesem Gerät gelöscht. Gespeichert seit{' '}
        {fmtDateTime(expired[0]?.createdAt)}. Bitte bei Bedarf neu melden.
      </Notice>
      {failed ? <Notice tone="warn">{DEVICE_FAILURE}</Notice> : null}
      <Button variant="outline" disabled={busy} onClick={() => void act(confirmExpired)}>
        Bestätigen
      </Button>
    </div>
  );
}
